import { Button, Checkbox, Modal, Spinner } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { FormError, FormField } from '@/components';
import { getInitials } from '@/lib/utils';
import { forgetAccount, knownAccounts } from '../accounts';
import { AUTH_ROUTES } from '../constants';
import { useLogin, useLoginWithGoogle } from '../hooks';
import { type LoginFormValues, loginSchema } from '../schemas';
import { toErrorMessage, toSocialErrorMessage } from '../utils';
import { AccountPicker } from './account-picker';
import { GoogleSignInButton } from './google-sign-in-button';

export function LoginForm({
  defaultEmail = '',
  useRemembered = true,
  onSuccess,
}: {
  defaultEmail?: string;
  useRemembered?: boolean;
  onSuccess?: () => void;
} = {}) {
  const login = useLogin();
  const loginWithGoogle = useLoginWithGoogle();
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  /**
   * Quem ja entrou neste navegador volta com a conta na tela: falta a senha,
   * nao a identidade. Trocar de conta continua a um clique.
   */
  const [contas, setContas] = useState(() => knownAccounts());
  const [lembrada, setLembrada] = useState(() =>
    defaultEmail || !useRemembered ? null : (contas[0] ?? null),
  );
  const [escolhendo, setEscolhendo] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: defaultEmail || (lembrada?.email ?? ''),
      password: '',
      rememberMe: true,
    },
  });

  let formError: string | null = null;
  if (login.error) formError = toErrorMessage(login.error);
  else if (loginWithGoogle.error) formError = toSocialErrorMessage(loginWithGoogle.error);

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit((values) => login.mutate(values, { onSuccess }))}
      noValidate
    >
      <FormError message={formError} />

      {lembrada ? (
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-muted/40 px-3 py-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {getInitials(lembrada.name)}
          </span>

          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{lembrada.name}</span>
            <span className="block truncate text-xs text-muted-foreground">{lembrada.email}</span>
          </span>

          <button
            type="button"
            className="flex shrink-0 items-center gap-1.5 text-sm text-primary hover:underline"
            onClick={() => setEscolhendo(true)}
          >
            <ArrowLeftRight className="h-4 w-4" />
            Trocar
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <FormField
            label="E-mail"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="voce@empresa.com"
            error={errors.email?.message}
            {...register('email')}
          />

          {/* Digitar o email de novo e o caminho longo quando a conta ja
              esteve aqui: a lista fica a um clique. */}
          {contas.length > 0 ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              onClick={() => setEscolhendo(true)}
            >
              <ArrowLeftRight className="h-4 w-4" />
              Escolher uma conta guardada
            </button>
          ) : null}
        </div>
      )}

      <div className="space-y-2">
        <FormField
          label="Senha"
          type="password"
          autoComplete="current-password"
          placeholder="Sua senha"
          error={errors.password?.message}
          {...register('password')}
        />

        {/* O link mora colado ao campo: quem esqueceu a senha percebe aqui, e
            nao depois de tentar entrar. */}
        <div className="flex justify-end">
          <Link
            to={AUTH_ROUTES.FORGOT_PASSWORD}
            className="text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            Esqueci minha senha
          </Link>
        </div>
      </div>

      <Checkbox label="Manter conectado" {...register('rememberMe')} />

      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? <Spinner /> : null}
        Entrar
      </Button>

      {googleClientId ? (
        <>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            ou
            <span className="h-px flex-1 bg-border" />
          </div>

          <GoogleSignInButton
            clientId={googleClientId}
            disabled={loginWithGoogle.isPending}
            onCredential={(credential) => loginWithGoogle.mutate(credential, { onSuccess })}
          />
        </>
      ) : null}
      {escolhendo ? (
        <Modal
          open
          onClose={() => setEscolhendo(false)}
          title="Escolher conta"
          subtitle="Entre com uma das contas ja usadas neste navegador"
          size="small"
          flushBody
          // Tela cheia no celular, janela no desktop.
          className="h-[100dvh] max-h-none rounded-none sm:h-auto sm:max-h-[90dvh] sm:max-w-sm sm:rounded-lg"
        >
          <AccountPicker
            contas={contas}
            onPick={(conta) => {
              const escolhida = contas.find((item) => item.id === conta.id) ?? null;
              setLembrada(escolhida);
              setValue('email', conta.email);
              setEscolhendo(false);
            }}
            onNova={() => {
              setLembrada(null);
              setValue('email', '');
              setEscolhendo(false);
            }}
            onRemove={(conta) => {
              forgetAccount(conta.id);
              const restantes = knownAccounts();
              setContas(restantes);
              if (lembrada?.id === conta.id) setLembrada(restantes[0] ?? null);
              if (restantes.length === 0) setEscolhendo(false);
            }}
          />
        </Modal>
      ) : null}
    </form>
  );
}
